import { useOnlineStatus } from '../../shared/hooks/useOnlineStatus';
import { Button } from '../../shared/ui/Button';
import { useAuth } from '../AuthProvider';

import s from './OfflineBanner.module.css';

/**
 * One slim line when the network is gone or the server cannot be reached.
 *
 * Two different situations with two different sentences: no connection at
 * all (nothing to retry), or a connection that cannot reach Supabase (worth a
 * "try now"). Per-screen query errors still show inline; this is the ambient
 * explanation for why they are all failing at once. Exam routes render outside
 * the shell, so it never stacks on the exam's own save indicator.
 */
export function OfflineBanner(): JSX.Element | null {
  const online = useOnlineStatus();
  const { unreachable, retry } = useAuth();
  if (online && !unreachable) return null;

  return (
    <div className={s.root} role="status">
      <svg
        className={s.icon}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8 2 14.5 13H1.5L8 2zM8 6.5v3M8 11.5h.01" />
      </svg>
      <p className={s.text}>
        {online
          ? 'Can’t reach the server — retrying…'
          : 'You’re offline. Some actions won’t work until you reconnect.'}
      </p>
      {online ? (
        <Button variant="ghost" size="sm" className={s.action} onClick={retry}>
          Try now
        </Button>
      ) : null}
    </div>
  );
}
